/**
 * Policies: every document the organization asks its clients to read.
 *
 * All of them are created here and stored by the API — the Terms of Service,
 * the privacy notice, a channel's acceptable-use policy — so changing what
 * clients agree to never needs a release. What each row shows is the version
 * clients see now, anything scheduled to replace it, and whether a draft is in
 * progress, because those are the three states somebody needs before editing.
 */

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { FileText, Globe, Plus } from 'lucide-react';
import {
  ApiError,
  Badge,
  Button,
  Can,
  Card,
  EmptyState,
  ErrorState,
  formatDate,
  LoadingState,
  PageHeader,
  ScrollArea,
  StatusChip,
} from '@connect/web-shared';

import { DocumentFormDialog } from './document-form-dialog';
import { STATUS_TONES, fetchDocuments, fetchOptions, labelOf, legalKeys } from './types';

export function LegalDocumentsPage() {
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const options = useQuery({ queryKey: legalKeys.options, queryFn: fetchOptions, staleTime: Infinity });
  const documents = useQuery({ queryKey: legalKeys.documents, queryFn: fetchDocuments });

  return (
    <div className="space-y-4">
      <PageHeader
        title="Policies"
        description="Terms, privacy notices and channel policies. Each change is a new version; published versions never change."
        breadcrumbs={[{ label: 'Legal' }, { label: 'Policies' }]}
        actions={
          <Can permission="core.legal_document.create">
            <Button onClick={() => setCreating(true)}>
              <Plus className="size-4" />
              New document
            </Button>
          </Can>
        }
      />

      <Card>
        {documents.isLoading ? (
          <LoadingState label="Loading policies" />
        ) : documents.error ? (
          <ErrorState
            message={documents.error instanceof ApiError ? documents.error.message : undefined}
            onRetry={() => void documents.refetch()}
          />
        ) : !documents.data?.length ? (
          <EmptyState
            icon={<FileText className="size-5" />}
            title="No policies yet"
            description="Create a document, write its first version and publish it. Then ask clients to accept it under Consent requirements."
          />
        ) : (
          <ScrollArea className="">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-line text-left text-2xs uppercase tracking-wide text-faint">
                  <th className="px-4 py-2 font-medium">Document</th>
                  <th className="px-4 py-2 font-medium">Kind</th>
                  <th className="px-4 py-2 font-medium">In effect</th>
                  <th className="px-4 py-2 font-medium">Next</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {documents.data.map((doc) => (
                  <tr
                    key={doc.public_id}
                    className="cursor-pointer hover:bg-surface-2"
                    onClick={() => void navigate(`/legal/documents/${doc.public_id}`)}
                  >
                    <td className="px-4 py-2.5">
                      <Link
                        to={`/legal/documents/${doc.public_id}`}
                        className="font-medium text-heading hover:text-accent"
                        onClick={(event) => event.stopPropagation()}
                      >
                        {doc.title}
                      </Link>
                      <p className="flex items-center gap-1.5 font-mono text-2xs text-faint">
                        {doc.key}
                        {doc.is_public ? (
                          <Globe className="size-3" aria-label="Public" />
                        ) : null}
                      </p>
                    </td>
                    <td className="px-4 py-2.5 text-muted">
                      {labelOf(options.data?.document_kinds, doc.kind)}
                    </td>
                    <td className="px-4 py-2.5">
                      {doc.current_version ? (
                        <span>
                          {doc.current_version.label}
                          <span className="text-faint">
                            {' '}
                            · since {formatDate(doc.current_version.effective_at)}
                          </span>
                        </span>
                      ) : (
                        <span className="text-faint">Nothing published</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      {doc.scheduled_version ? (
                        <Badge tone="info">
                          {doc.scheduled_version.label} from{' '}
                          {formatDate(doc.scheduled_version.effective_at)}
                        </Badge>
                      ) : doc.has_draft ? (
                        <Badge tone="warning">Draft in progress</Badge>
                      ) : (
                        <span className="text-faint">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      <StatusChip value={doc.status} tones={STATUS_TONES} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ScrollArea>
        )}
      </Card>

      <DocumentFormDialog
        open={creating}
        onClose={() => setCreating(false)}
        document={null}
        options={options.data}
        onSaved={(saved) => void navigate(`/legal/documents/${saved.public_id}`)}
      />
    </div>
  );
}
